import { useGetAccountStatesQueryOptions } from "@/api/account-state/account-state"
import { useGetFeatureFlagsQueryOptions } from "@/api/config/config"
import { useGetCustomerQueryOptions } from "@/api/customer/customer"
import { FlexMarketSubscriptionState } from "@/api/model"
import { MyAccountRoute } from "@/constants/routes/myAccount"
import nativeHelper from "@/lib/nativeHelper"
import ErrorReporter from "@/services/ErrorReporter"
import { useQueryClient } from "@tanstack/vue-query"
import { NavigationGuard } from "vue-router"

const hasFlexMarketSubscription = (
  subscriptionState: FlexMarketSubscriptionState,
) => {
  return (
    subscriptionState === FlexMarketSubscriptionState.active ||
    subscriptionState === FlexMarketSubscriptionState.activeNoAssets
  )
}

export const flexMarketSubscriptionGuard: NavigationGuard = async (to) => {
  const queryClient = useQueryClient()

  try {
    const featureFlagsQueryOptions = useGetFeatureFlagsQueryOptions()
    const featureFlags = await queryClient.ensureQueryData(
      featureFlagsQueryOptions,
    )
    const showSoCharged = nativeHelper.isNative()
      ? featureFlags.showSoChargedMobileApp
      : featureFlags.showSoChargedWeb

    if (!showSoCharged) {
      return { path: MyAccountRoute.home }
    }

    const customerQueryOptions = useGetCustomerQueryOptions()
    const accountId = (await queryClient.ensureQueryData(customerQueryOptions))
      .accountIDLastVisited

    if (!accountId) throw new Error("accountId not found")

    const accountQueryOptions = useGetAccountStatesQueryOptions(accountId)
    const { flexMarketSubscription } =
      await queryClient.ensureQueryData(accountQueryOptions)

    // redirect to home if customer is not eligible for flex market subscription
    if (flexMarketSubscription === FlexMarketSubscriptionState.notEligible) {
      return { path: MyAccountRoute.home }
    }

    // redirect to app if user already has flex market subscription
    if (
      (to.path === MyAccountRoute.flexMarketSubscriptionDetails ||
        to.path === MyAccountRoute.flexMarketSubscriptionSignUp) &&
      hasFlexMarketSubscription(flexMarketSubscription)
    ) {
      return { path: MyAccountRoute.flexMarketSubscriptionApp }
    }

    // redirect to details if user doesn't have flex market subscription
    if (
      to.path === MyAccountRoute.flexMarketSubscriptionApp &&
      !hasFlexMarketSubscription(flexMarketSubscription)
    ) {
      return { path: MyAccountRoute.flexMarketSubscriptionDetails }
    }
  } catch (error) {
    console.error("flexMarketSubscriptionGuard: ", error)
    ErrorReporter.report(error)
    return { path: MyAccountRoute.home }
  }
}
